import { useCallback, useEffect, useRef } from 'react'
import { midTermFeatures } from '../filters/filtersMenu/constants/midTermFeatures'

const useMidTermFilters = () => {
  const featureQueryKeys = midTermFeatures.flatMap(section =>
    section.list.map(item => item.query),
  )

  const baseFilterKeys = [
    'village',
    'bedroom_count',
    'guests',
    'min_price',
    'max_price',
  ]
  const protectedKeys = [
    'checkin',
    'checkout',
    'page',
    'co_ordinates',
    'sortBy',
    'phrase',
    'near_cities[]',
  ]
  const allFilterKeys = [...featureQueryKeys, ...baseFilterKeys]
  const allUnRemovableFilters = [...allFilterKeys, ...protectedKeys]
  const [default_min, default_max] = [100000, 300000000]
  const getInitialValues = (queryParams: any) => {
    const minPrice = queryParams.min_price
      ? Number(queryParams.min_price) * 1000
      : default_min
    const maxPrice = queryParams.max_price
      ? Number(queryParams.max_price) * 1000
      : default_max
    const defaultValues: Record<string, any> = {
      village: queryParams.village || [],
      bedroom_count: queryParams.bedroom_count
        ? Number(queryParams.bedroom_count)
        : 0,
      guests: queryParams.guests ? Number(queryParams.guests) : 0,
      price: [minPrice, maxPrice],
    }

    midTermFeatures.forEach(group => {
      group.list.forEach(item => {
        const key = item.query
        const value = queryParams[key]
        defaultValues[key] =
          value !== undefined && value !== null && value !== ''
            ? item.qValue
            : undefined
      })
    })

    return defaultValues
  }

  const extractActiveFilters = (queryParams: any) => {
    return allFilterKeys.reduce(
      (acc, key) => {
        let value = queryParams[key]

        if (key === 'village') {
          value = Array.isArray(value)
            ? value
            : value !== undefined
              ? [value]
              : []
        }
        if (
          value !== undefined &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          acc[key] = value
        }

        return acc
      },
      {} as Record<string, any>,
    )
  }

  const clearFilters = (queryParams: any, keysToClear?: string | string[]) => {
    const result = { ...queryParams }
    if (typeof keysToClear === 'string') {
      result[keysToClear] = undefined
    } else if (Array.isArray(keysToClear)) {
      keysToClear.forEach(key => {
        result[key] = undefined
      })
    } else {
      allFilterKeys.forEach(key => (result[key] = undefined))
    }
    return result
  }

  const updateQueryParams = useCallback(
    (form: any, setQuery: any) => {
      const rawQuery: Record<string, any> = {}

      Object.entries(form).forEach(([key, value]) => {
        const isEmpty =
          value === undefined ||
          value === null ||
          (Array.isArray(value) && value.length === 0) ||
          value === ''

        if (key === 'price' && Array.isArray(value)) {
          const [min, max] = value

          rawQuery['min_price'] = min !== default_min ? min / 1000 : undefined
          rawQuery['max_price'] = max !== default_max ? max / 1000 : undefined

          return
        }

        if (isEmpty) {
          rawQuery[key] = undefined
          return
        }

        if (key === 'village') {
          rawQuery[key] = Array.isArray(value) ? value : [value]
          return
        }

        const feature = midTermFeatures
          .flatMap(group => group.list)
          .find(item => item.query === key)
        if (isEmpty || value === false || value === 0) {
          rawQuery[key] = undefined
        } else {
          rawQuery[key] = feature?.qValue ?? value
        }
      })

      setQuery(rawQuery)
    },
    [midTermFeatures],
  )

  const useSyncFormWithQuery = (
    queries: Record<string, any>,
    reset: (values: any) => void,
  ) => {
    const prevQueryStringRef = useRef('')

    useEffect(() => {
      if (!queries || Object.keys(queries).length === 0) return

      const currentQueryString = JSON.stringify(queries)
      if (prevQueryStringRef.current !== currentQueryString) {
        prevQueryStringRef.current = currentQueryString
        const initialValues = getInitialValues(queries)
        reset(initialValues)
      }
    }, [queries, reset])
  }

  return {
    midTermFeatures,
    getInitialValues,
    extractActiveFilters,
    clearFilters,
    updateQueryParams,
    allFilterKeys,
    useSyncFormWithQuery,
    allUnRemovableFilters,
  }
}
export default useMidTermFilters
